import resumeService from 'ApiService/SuperAdmin/resumeService';
import studentAssignmentServices from 'ApiService/Placement/studentAssignmentServices';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FaDownload, FaEye } from 'react-icons/fa';
import { BallTriangle } from 'react-loader-spinner';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const EMPTY_FILTERS = {
  search: '',
  hrId: '',
  companyId: '',
  branchId: '',
  resumeType: '',
};

const STATUS_COLORS = {
  Pending: 'bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300',
  'Interview Scheduled': 'bg-blue-100 text-blue-700',
  'Interview Attended': 'bg-blue-100 text-blue-700',
  Selected: 'bg-green-100 text-green-700',
  'Offer Accepted': 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  'Offer Declined': 'bg-red-100 text-red-700',
  'On Hold': 'bg-yellow-100 text-yellow-700',
};

const selectClass =
  'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white';

const Index = () => {
  const { state } = useContext(AuthContext);

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    hrs: [],
    companies: [],
    branches: [],
  });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const totalPages = Math.max(Math.ceil(total / limit), 1);

  // 🔹 FETCH FILTER OPTIONS
  useEffect(() => {
    if (!state?.user) return;
    resumeService
      .getResumeFilters()
      .then((response) => {
        if (response.data.success) setFilterOptions(response.data.data);
      })
      .catch((error) =>
        toast.error(
          error?.response?.data?.message || 'Failed to fetch filter options'
        )
      );
  }, [state?.user]);

  // 🔹 DEBOUNCE SEARCH
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput.trim()
          ? prev
          : { ...prev, search: searchInput.trim() }
      );
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 🔹 FETCH RESUMES
  const fetchResumes = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const response = await resumeService.getAllResumes(params);
      if (response.data.success) {
        setStudents(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch resumes');
      setStudents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state?.user) fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.user, filters, page, limit]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  // 🔹 RESUME HELPERS
  const getTypedResume = (student, type) =>
    student.resumes?.find((r) => r.resumeType === type) || null;

  const getDisplayResumes = (student) => {
    const fresher = getTypedResume(student, 'Fresher');
    const experience = getTypedResume(student, 'Experience');
    if (fresher || experience) {
      return [
        ...(fresher ? [{ label: 'Fresher', resume: fresher }] : []),
        ...(experience ? [{ label: 'Experience', resume: experience }] : []),
      ];
    }
    return student.resume?.filePath
      ? [{ label: 'Resume', resume: student.resume }]
      : [];
  };

  const getResumeUrl = (resume) =>
    `${process.env.REACT_APP_API_BASE_URL}/${resume.filePath}`;

  const handleViewResume = (resume) => {
    window.open(getResumeUrl(resume), '_blank', 'noopener,noreferrer');
  };

  const handleDownloadResume = async (resume) => {
    try {
      const response = await fetch(getResumeUrl(resume));
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = resume.fileName || 'resume';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download resume.');
    }
  };

  // 🔹 SELECTION
  const pageIds = students.map((s) => s._id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectPage = () => {
    setSelectedIds((prev) =>
      allPageSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])]
    );
  };

  const handleDownloadZip = async () => {
    if (selectedIds.length === 0) {
      setShowDownloadModal(false);
      return toast.error('No students selected');
    }
    setDownloadingZip(true);
    try {
      const response = await studentAssignmentServices.downloadResumes(
        selectedIds
      );
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'resumes.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowDownloadModal(false);
      toast.success('Resumes downloaded successfully.');
    } catch (error) {
      // responseType is blob, so the error message arrives as a Blob.
      let message = 'Failed to download resumes';
      try {
        const text = await error?.response?.data?.text?.();
        message = JSON.parse(text)?.message || message;
      } catch {}
      toast.error(message);
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className="mt-10 h-full w-full rounded-[20px] bg-white p-4 dark:bg-navy-800 md:mt-5">
      {/* 🔹 HEADER */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            All Resumes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {total} student{total === 1 ? '' : 's'} with resumes
          </p>
        </div>
        <button
          onClick={() => setShowDownloadModal(true)}
          disabled={selectedIds.length === 0}
          className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          <FaDownload /> Download ZIP ({selectedIds.length})
        </button>
      </div>

      {/* 🔹 FILTERS */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name / email / phone"
          className={`${selectClass} lg:col-span-2`}
        />
        <select
          className={selectClass}
          value={filters.hrId}
          onChange={(e) => handleFilterChange('hrId', e.target.value)}
        >
          <option value="">All HRs</option>
          {filterOptions.hrs.map((hr) => (
            <option key={hr._id} value={hr._id}>
              {hr.name} ({hr.hrRole})
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={filters.companyId}
          onChange={(e) => handleFilterChange('companyId', e.target.value)}
        >
          <option value="">All Companies</option>
          {filterOptions.companies.map((company) => (
            <option key={company._id} value={company._id}>
              {company.name}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={filters.branchId}
          onChange={(e) => handleFilterChange('branchId', e.target.value)}
        >
          <option value="">All Branches</option>
          {filterOptions.branches.map((branch) => (
            <option key={branch._id} value={branch._id}>
              {branch.name}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <select
            className={selectClass}
            value={filters.resumeType}
            onChange={(e) => handleFilterChange('resumeType', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="Fresher">Fresher</option>
            <option value="Experience">Experience</option>
          </select>
          <button
            onClick={handleReset}
            className="rounded-lg border px-3 py-2 text-sm dark:text-white"
          >
            Reset
          </button>
        </div>
      </div>

      {/* 🔹 TABLE */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <BallTriangle color="#5431ed" />
        </div>
      ) : students.length === 0 ? (
        <div className="flex justify-center">
          <Nodatafound title="No resumes match these filters" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-white/10">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-400">
            <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-navy-700 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectPage}
                  />
                </th>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Assigned HR</th>
                <th className="px-4 py-3">Companies</th>
                <th className="px-4 py-3">Resume</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr
                  key={student._id}
                  className="border-b align-top dark:border-white/10"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(student._id)}
                      onChange={() => toggleSelect(student._id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {student.name}
                    </p>
                    <p className="text-xs">{student.email}</p>
                    <p className="text-xs">{student.contactNumber}</p>
                  </td>
                  <td className="px-4 py-3">{student.branch?.name || '—'}</td>
                  <td className="px-4 py-3">
                    {student.hrs?.length ? (
                      student.hrs.map((hr) => (
                        <p key={hr._id} className="whitespace-nowrap">
                          {hr.name}{' '}
                          <span className="text-xs text-gray-500">
                            ({hr.hrRole})
                          </span>
                        </p>
                      ))
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {student.companies?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {student.companies.map((company, i) => (
                          <span
                            key={`${company._id}-${i}`}
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${
                              STATUS_COLORS[company.status] ||
                              STATUS_COLORS.Pending
                            } ${company.isActive ? '' : 'opacity-60'}`}
                          >
                            {company.name} · {company.status}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {getDisplayResumes(student).map(({ label, resume }) => (
                      <div
                        key={label}
                        className="mb-1 flex items-center gap-3 whitespace-nowrap"
                      >
                        <span className="w-20 text-xs font-medium">
                          {label}
                        </span>
                        <button
                          onClick={() => handleViewResume(resume)}
                          title={`View ${resume.fileName || 'resume'}`}
                          className="text-brand-500"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleDownloadResume(resume)}
                          title={`Download ${resume.fileName || 'resume'}`}
                          className="text-green-600"
                        >
                          <FaDownload />
                        </button>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔹 PAGINATION */}
      {total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm dark:text-white">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1 || loading}
            className="rounded-lg border px-3 py-1 disabled:opacity-50"
          >
            « Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages || loading}
            className="rounded-lg border px-3 py-1 disabled:opacity-50"
          >
            Next »
          </button>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-lg border px-2 py-1 dark:!border-white/10 dark:!bg-navy-800"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 🔹 ZIP CONFIRM MODAL */}
      {showDownloadModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 dark:bg-navy-800">
            <h3 className="mb-4 text-lg font-semibold dark:text-white">
              Download Resumes
            </h3>
            <p className="mb-6 text-sm dark:text-gray-300">
              You have selected {selectedIds.length} student
              {selectedIds.length === 1 ? '' : 's'}. Download their resumes
              as a ZIP file?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDownloadModal(false)}
                disabled={downloadingZip}
                className="rounded-lg border px-4 py-2 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadZip}
                disabled={downloadingZip}
                className="rounded-lg bg-brand-500 px-4 py-2 text-white disabled:opacity-50"
              >
                {downloadingZip ? 'Downloading...' : 'Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
